import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { AlertTriangle, ArrowUpRight, Star } from "lucide-react";
import { fmtRelative } from "@/lib/format";

export default function QualityReview() {
  const { activeWorkspaceId } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!activeWorkspaceId) return;
      setBusy(true);
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/quality-review`);
        setItems(data || []);
      } finally { setBusy(false); }
    };
    load();
  }, [activeWorkspaceId]);

  return (
    <>
      <PageHeader
        eyebrow="Design quality"
        title="Quality review"
        description="Items rated below 3★ on average, or with recent negative feedback. Use this to coach, rework, or escalate."
        testid="quality-header"
      />
      <div className="px-4 sm:px-6 md:px-10 pb-8">
        {busy ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState title="All clear" description="No low-rated items right now. Keep up the great work." icon={AlertTriangle} testid="quality-empty" />
        ) : (
          <ul className="space-y-3" data-testid="quality-list">
            {items.map((it) => (
              <li key={it.review_item_id} className="border border-border p-4" data-testid={`quality-item-${it.review_item_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold truncate" style={{ fontFamily: "var(--font-heading)" }}>{it.title || "(untitled)"}</span>
                      <span className="chip chip-pending text-[10px]">{it.item_type}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-400 fill-current" />
                        <strong className="text-foreground">{it.avg_stars}</strong> · {it.rating_count} rating{it.rating_count === 1 ? "" : "s"}
                      </span>
                    </div>
                  </div>
                  <Link to={`/app/review-items/${it.review_item_id}`} className="text-primary inline-flex items-center text-xs hover:underline shrink-0" data-testid={`quality-open-${it.review_item_id}`}>
                    Open <ArrowUpRight className="w-3 h-3 ml-1" />
                  </Link>
                </div>
                {it.ratings && it.ratings.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {it.ratings.slice().reverse().slice(0, 3).map((r, i) => (
                      <li key={i} className="border-l-2 border-primary/40 pl-3 text-xs" data-testid={`quality-rating-${it.review_item_id}-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{r.rater_name}</span>
                          <span className="chip text-[9px]">{r.kind}</span>
                          <span className="inline-flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star key={s} className={`w-3 h-3 ${s <= r.stars ? "text-amber-400 fill-current" : "text-muted-foreground"}`} />
                            ))}
                          </span>
                          <span className="text-[10px] text-muted-foreground ml-auto">{fmtRelative(r.created_at)}</span>
                        </div>
                        {r.comment && <div className="text-muted-foreground mt-1 whitespace-pre-wrap">{r.comment}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
